import { computed, reactive, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { HomeElevationType, UploadStatus } from '@/types/propertyTaxUpload/propertyTaxUpload'
import type { FlightProjectData, UploadFileData } from '@/types/propertyTaxUpload/propertyTaxUpload'
import { useFileUploadApi } from '@/api/dup/fileUpload/fileUploadApi'
import { useMiiaFwStore } from './miiaFwStore'
import MiiaFwConfig from '@/configs/config'
import { useRunFileProcessorEcsApi } from '@/api/dup/runFileProcessorEcs/runFileProcessorEcs'
import { DupCategoryId } from '@/types/api/dup/runFileProcessorEcs/runFileProcessorEcs'
import { useProjectApi } from '@/api/propertyTax/project/projectApi'
import { parallelChunksUpload } from '@/composables/common/parallelChunksUpload/parallelChunksUpload'

export const usePropertyTaxUploadStore = defineStore('propertyTaxUploadStore', () => {
  const miiaFwStore = useMiiaFwStore()
  const { govCode } = storeToRefs(miiaFwStore)

  const {
    createMultipartUpload,
    getPresignedUrl,
    uploadChunkWithProgress,
    cancelChunkUpload,
    completeMultipartUpload
  } = useFileUploadApi()
  const { runForPropscr } = useRunFileProcessorEcsApi()
  const { getProjectList } = useProjectApi()

  /** アップロード中フラグ(true: アップロード中, false: それ以外) */
  const isUploading = ref<boolean>(false)
  /** アップロード対象データ */
  const uploadFileList = ref<UploadFileData[]>([])

  /** フライト開始地点情報 */
  const flightProjectData = ref<FlightProjectData>({
    projectId: undefined,
    projectList: [],
    flightAltitude: undefined,
    homeElevationType: HomeElevationType.NOT_APPLICABLE, // 初期値
    homeElevationLatlng: {
      latitude: undefined,
      longitude: undefined
    },
    homeElevationValue: undefined,
    homeElevationImgFileName: ''
  })

  /** バリデーションエラー */
  const validateErrors = reactive({
    homeElevationLatlng: '',
    homeElevationValue: '',
    homeElevationImgFileName: ''
  })

  /** projectIdからプロジェクト名を高速に参照できるMapを作成 */
  const projectNameMap = computed(() => {
    const map = new Map<number, string>()
    flightProjectData.value.projectList.forEach(proj => {
      map.set(proj.id, proj.name)
    })
    return map
  })

  /**
   * プロジェクト名取得
   * @param projectId プロジェクトID
   * @returns プロジェクト名
   */
  const getProjectName = (projectId: number | undefined) =>
    projectId ? projectNameMap.value.get(projectId) : undefined

  /** ファイル識別用のキーを生成する */
  const generateFileKey = (): string => {
    const timestamp = Date.now().toString(36)
    const randomStr = Math.random().toString(36).substring(2, 7)
    return `${timestamp}-${randomStr}`
  }

  /**
   * プロジェクト情報のGETリクエストを実行する関数
   * @returns APIレスポンスのdata
   */
  const fetchGetProjects = async () => {
    flightProjectData.value.projectList = await getProjectList()
    if (flightProjectData.value.projectList.length > 0) {
      flightProjectData.value.projectId = flightProjectData.value.projectList[0].id
    }
  }

  /**
   * アップロード対象に追加する。
   * @param files 対象データ
   */
  const addFiles = (...files: File[]) => {
    if (isUploading.value) {
      return
    }
    uploadFileList.value.push(
      ...files.map(file => ({
        key: generateFileKey(),
        fileName: file.name,
        file: file,
        status: UploadStatus.WAIT,
        progress: 0,
        projectId: undefined
      }))
    )
  }

  /** アップロード対象をリセットする。 */
  const clearFiles = () => {
    if (isUploading.value) {
      return
    }
    uploadFileList.value.splice(0)
  }

  /** アップロードを中断する */
  const abortUpload = () => {
    if (!isUploading.value) {
      return
    }
    uploadFileList.value
      .filter(data => data.status !== UploadStatus.COMPLETE)
      .forEach(data => {
        cancelChunkUpload(data.key)
      })
    uploadFileList.value = uploadFileList.value.filter(
      data => data.status === UploadStatus.COMPLETE
    )

    isUploading.value = false
  }

  /** ファイルの一括アップロードを実行する。 */
  const executeUpload = async () => {
    const fileDataList = uploadFileList.value.filter(data => data.status !== UploadStatus.COMPLETE)
    if (fileDataList.length === 0 || isUploading.value) {
      return
    }

    isUploading.value = true
    try {
      fileDataList.forEach(data => {
        data.progress = 0
        data.status = UploadStatus.WAITING_UPLOAD
        data.completedDateTime = undefined
        data.projectId = flightProjectData.value.projectId
      })

      const CONCURRENT_LIMIT = 3
      let currentIndex = 0

      // 実際のアップロード処理を行う関数
      const uploadNext = async (): Promise<void> => {
        if (currentIndex >= fileDataList.length) return // 全ての処理が割り当てられたら終了

        const fileData = fileDataList[currentIndex]
        currentIndex++
        fileData.status = UploadStatus.UPLOADING

        try {
          await executeMultiPartUpload(fileData)
          fileData.progress = 100
          fileData.status = UploadStatus.COMPLETE
          fileData.completedDateTime = new Date()
        } catch (e) {
          console.error(e)
          fileData.status = UploadStatus.ERROR
        }

        await uploadNext()
      }

      // 最初にCONCURRENT_LIMIT個の処理を同時に開始する
      await Promise.all(
        Array(CONCURRENT_LIMIT)
          .fill(null)
          .map(() => uploadNext())
      )

      return !fileDataList.some(e => e.status === UploadStatus.ERROR)
    } finally {
      isUploading.value = false
    }
  }

  /**
   * マルチパートアップロードを実行する
   * @param fileData アップロード対象のファイルデータ
   */
  const executeMultiPartUpload = async (fileData: UploadFileData) => {
    const commonParam = {
      system: MiiaFwConfig.config.sysId,
      govCode: govCode.value!,
      projectId: flightProjectData.value.projectId,
      dupCategoryId: DupCategoryId.PROPERTY_TAX_SCREENING
    } as const

    const { key: uploadKey, uploadId } = await createMultipartUpload({
      ...commonParam,
      fileName: fileData.fileName
    })

    const multipartList = await parallelChunksUpload(
      fileData,
      commonParam,
      uploadKey,
      uploadId,
      getPresignedUrl,
      uploadChunkWithProgress
    )

    // マルチパートアップロード完了通知
    await completeMultipartUpload({
      ...commonParam,
      key: uploadKey,
      uploadId,
      multipartList
    })
    const paramForPropscr = createParams(uploadKey)
    // ECS実行
    await runForPropscr(paramForPropscr)

    return fileData
  }

  /** リクエストパラメータを作成する */
  function createParams(uploadKey: string) {
    const baseParam = {
      system: MiiaFwConfig.config.sysId,
      govCode: govCode.value!,
      projectId: flightProjectData.value.projectId,
      dupCategoryId: DupCategoryId.PROPERTY_TAX_SCREENING,
      flightAltitude: flightProjectData.value.flightAltitude,
      key: uploadKey
    }
    if (!flightProjectData.value.flightAltitude) {
      delete baseParam.flightAltitude
    }
    switch (flightProjectData.value.homeElevationType) {
      case HomeElevationType.LATLNG:
        return {
          ...baseParam,
          homeElevationType: HomeElevationType.LATLNG,
          homeElevationLatlng: {
            latitude: flightProjectData.value.homeElevationLatlng.latitude,
            longitude: flightProjectData.value.homeElevationLatlng.longitude
          }
        }
      case HomeElevationType.ALTITUDE:
        return {
          ...baseParam,
          homeElevationType: HomeElevationType.ALTITUDE,
          homeElevationValue: flightProjectData.value.homeElevationValue
        }
      case HomeElevationType.FILE_NAME:
        return {
          ...baseParam,
          homeElevationType: HomeElevationType.FILE_NAME,
          homeElevationImgFileName: flightProjectData.value.homeElevationImgFileName
        }
      default:
        return {
          ...baseParam,
          homeElevationType: HomeElevationType.NOT_APPLICABLE
        }
    }
  }

  /**
   * バリデーションエラーをリセットする
   */
  const resetValidateErrors = () => {
    Object.assign(validateErrors, {
      homeElevationLatlng: '',
      homeElevationValue: '',
      homeElevationImgFileName: ''
    })
  }

  /** ストアの状態をリセットする */
  const reset = () => {
    isUploading.value = false
    uploadFileList.value.splice(0)
    flightProjectData.value = {
      projectId: undefined,
      projectList: [],
      flightAltitude: undefined,
      homeElevationType: HomeElevationType.NOT_APPLICABLE,
      homeElevationLatlng: {
        latitude: undefined,
        longitude: undefined
      },
      homeElevationValue: undefined,
      homeElevationImgFileName: ''
    }
    resetValidateErrors()
  }

  return {
    isUploading,
    uploadFileList,
    flightProjectData,
    validateErrors,
    getProjectName,
    fetchGetProjects,
    addFiles,
    clearFiles,
    executeUpload,
    abortUpload,
    resetValidateErrors,
    reset
  }
})
